import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { IConversation } from '~/models/Conversation';
import { messageKeys } from '../messageKeys';
import { messagesService } from '../messagesService';

const LIMIT = 15;

interface IUseConversationsParams {
  filter: 'all' | 'unread';
  enabled?: boolean;
}

export function useConversations({ filter, enabled = true }: IUseConversationsParams) {
  const query = useInfiniteQuery({
    queryKey: messageKeys.conversations(filter),
    queryFn: ({ pageParam }) =>
      messagesService.getConversations({
        page: pageParam,
        limit: LIMIT,
        ...(filter === 'unread' ? { status: 'unread' as const } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.conversations.length, 0);
      const noMore =
        lastPage.conversations.length === 0 ||
        lastPage.conversations.length < lastPage.limit ||
        loaded >= lastPage.total;
      return noMore ? undefined : allPages.length + 1;
    },
    enabled,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  const conversations = useMemo<IConversation[]>(
    () => query.data?.pages.flatMap((p) => p.conversations) ?? [],
    [query.data?.pages],
  );

  const totalUnread = useMemo(
    () => query.data?.pages[query.data.pages.length - 1]?.unread ?? 0,
    [query.data?.pages],
  );

  return {
    ...query,
    conversations,
    totalUnread,
    isFinalPage: !query.hasNextPage,
  };
}
